import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {RouterModule} from '@angular/router';
import {NotificationModule} from '@drl/notification/notification.module';
import {
  CaregiverAdapter,
  PatientAdapter,
  PatientCaregiverAdapter,
} from '@drl/shared/adapters';
import {AlertsAdapter} from '@drl/shared/adapters/alerts-adapter.service';
import {AppointmentsAdapter} from '@drl/shared/adapters/appointments-adapter.service';
import {ChatMessageAdapter} from '@drl/shared/adapters/chat-message-adapter.service';
import {ChatNotificationAdapter} from '@drl/shared/adapters/chat-notification-adapter.service';
import {MessagesAdapter} from '@drl/shared/adapters/messages-adapter.service';
import {MessagesCareCentralFacadeService} from '@drl/shared/facades/messages-care-central-facade.service';
import {PhysicalActivityFacadeService} from '@drl/shared/facades/physical-activity-facade.service';
import {ThemeModule} from '@drl/theme/theme.module';
import {NbToggleModule} from '@nebular/theme';
import {NgSelectModule} from '@ng-select/ng-select';
import {TranslateModule} from '@ngx-translate/core';
import {ScrollToModule} from '@nicky-lenaers/ngx-scroll-to';
import {OwlDateTimeModule, OwlNativeDateTimeModule} from 'ng-pick-datetime';
import {ChartsModule} from 'ng2-charts';
import {BsDropdownModule} from 'ngx-bootstrap';
import {InfiniteScrollModule} from 'ngx-infinite-scroll';
import {NgxIntlTelInputModule} from 'ngx-intl-tel-input';
import {OwlModule} from 'ngx-owl-carousel';
import {NgxPermissionsModule} from 'ngx-permissions';
import {ScrollEventModule} from 'ngx-scroll-event';
import {ToastrModule} from 'ngx-toastr';
import {ConfirmationService} from 'primeng/api';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {DropdownModule} from 'primeng/dropdown';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelectModule} from 'primeng/multiselect';
import {TableModule} from 'primeng/table';

import {
  CareGroupWithMembersAdapter,
  TenantProfileAdapter,
  WorkingTimingsConfigsAdapter,
} from './adapters';
import {AlertsConfigAdapter} from './adapters/alerts-config-adapter.service';
import {MoodLogCountAdapter} from './adapters/mood-log-count-adapter.service';
import {UserPatchAdapter} from './adapters/user-patch-adapter.service';
import {
  ContentAdapterService,
  FilterModelAdapterService,
} from './components/cms-list/adapters';
import {CMSFacadeService} from './components/cms-list/cms-facade.service';
import {CmsListComponent} from './components/cms-list/cms-list.component';
import {HeaderComponent} from './components/header/header.component';
import {PatientDetailsCardComponent} from './components/patient-details-card/patient-details-card.component';
import {TextSearchComponent} from './components/text-search/text-search.component';
import {CareGroupFacadeService, ConfigurationsFacadeService, UserFacadeService} from './facades';
import {BulkUploadComponent} from './components/header/bulk-upload/bulk-upload.component';
import {BulkEhrDataFacadeService} from './facades/bulk-ehr-data-facade.service';
import {BulkUploadResultComponent} from './components/header/bulk-upload-result/bulk-upload-result.component';
import {DialogEditAppointmentComponent} from '../main/care-central/dialog-edit-appointment/dialog-edit-appointment.component';
import {NgxStarRatingModule} from 'ngx-star-rating';
import {DialogChatComponent} from '../main/care-central/dialog-chat/dialog-chat.component';
import {DialogFeedbackComponent} from '../main/care-central/dialog-feedback/dialog-feedback.component';
import {AddNewUserComponent} from '../main/users/add-new-user/add-new-user.component';
import {SmartechService} from './smartech/smartech.service';
import { FeatureConfigAdapter } from './adapters/feature-config-adapter.service';
import { AddNewRecordComponent } from '../main/data-upload/add-record-modal/add-record.component';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { PatientFacadeService } from '../main/patients/patients-facade.service';
import { MoodLogAdapter } from '../main/patients/shared/adapters/mood-log-adapter.service';
import { PatientPhrAdapter } from '../main/patients/shared/adapters/patient-phr.adapter';
import { PatientCGEhrAdapter } from '../main/patients/shared/adapters/patientcg-ehr.adapter';
import { PhrCategoriesAdapter } from '../main/patients/shared/adapters/phr-categories-adapter.service';
import { PhrTagsAdapter } from '../main/patients/shared/adapters/phr-tags-adapter.service';
import { HealthTasksAdapter } from '../main/patients/shared/adapters/health-tasks-adapter.service';
import { TherapyPlanAdapter } from '../main/patients/shared/adapters/therapy-plan-adapter.service';
import { AppointmentAdapter } from '../main/data-upload/shared/adapters/appointment-adapters.service';
import { ActiveSessionDialogComponent } from './components/active-session-dialog/active-session-dialog.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { HighlightPipe } from './highlight.pipe';

@NgModule({
  entryComponents: [
    BulkUploadComponent,
    BulkUploadResultComponent,
    DialogEditAppointmentComponent,
    DialogChatComponent,
    DialogFeedbackComponent,
    AddNewUserComponent,
    AddNewRecordComponent,
    ActiveSessionDialogComponent
  ],
  declarations: [
    HeaderComponent,
    TextSearchComponent,
    PatientDetailsCardComponent,
    CmsListComponent,
    BulkUploadComponent,
    BulkUploadResultComponent,
    DialogEditAppointmentComponent,
    DialogChatComponent,
    DialogFeedbackComponent,
    AddNewUserComponent,
    AddNewRecordComponent,
    ActiveSessionDialogComponent,
    HighlightPipe
  ],
  imports: [
    CommonModule,
    RouterModule,
    NgxPermissionsModule.forChild(),
    InputTextModule,
    TableModule,
    ConfirmDialogModule,
    DropdownModule,
    AutoCompleteModule,
    NgSelectModule,
    BsDropdownModule.forRoot(),
    NgxIntlTelInputModule,
    InfiniteScrollModule,
    OwlModule,
    MultiSelectModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    ToastrModule.forRoot({
      preventDuplicates: true,
    }),
    ChartsModule,
    NotificationModule,
    ThemeModule.forRoot(),
    ScrollToModule.forRoot(),
    ScrollEventModule,
    NbToggleModule,
    TranslateModule,
    NgxStarRatingModule,
    CalendarModule,
    FormsModule,
    RadioButtonModule,
    
  ],
  exports: [
    HeaderComponent,
    TextSearchComponent,
    InputTextModule,
    TableModule,
    ConfirmDialogModule,
    DropdownModule,
    AutoCompleteModule,
    BsDropdownModule,
    NgSelectModule,
    NgxIntlTelInputModule,
    InfiniteScrollModule,
    OwlModule,
    MultiSelectModule,
    PatientDetailsCardComponent,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    CmsListComponent,
    ScrollToModule,
    ScrollEventModule,
    NbToggleModule,
    TranslateModule,
    FormsModule,
    CalendarModule,
    ActiveSessionDialogComponent,
    HighlightPipe
  ],
  providers: [
    UserFacadeService,
    UserPatchAdapter,
    ConfirmationService,
    ConfigurationsFacadeService,
    TenantProfileAdapter,
    WorkingTimingsConfigsAdapter,
    MoodLogCountAdapter,
    CareGroupWithMembersAdapter,
    CMSFacadeService,
    FilterModelAdapterService,
    ContentAdapterService,
    AlertsConfigAdapter,
    PatientCaregiverAdapter,
    AppointmentsAdapter,
    MessagesAdapter,
    ChatMessageAdapter,
    ChatNotificationAdapter,
    MessagesCareCentralFacadeService,
    PhysicalActivityFacadeService,
    AlertsAdapter,
    PatientAdapter,
    CaregiverAdapter,
    BulkEhrDataFacadeService,
    SmartechService,
    CareGroupFacadeService,
    FeatureConfigAdapter,
    PatientFacadeService,
    MoodLogAdapter,
    PatientPhrAdapter,
    PatientCGEhrAdapter,
    PhrCategoriesAdapter,
    PhrTagsAdapter,
    HealthTasksAdapter,
    TherapyPlanAdapter,
    AppointmentAdapter
  ],
})
export class SharedModule {}
